import { useEffect, useMemo, useRef } from "react";
import type { Chart as ChartJS, ChartData, ChartOptions } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { ChartCanvasContainer } from "./Chart.styles";
import { ensureChartsRegistered } from "./chartRegistry";
import { renderExternalDoughnutTooltip } from "./externalDoughnutTooltip";

ensureChartsRegistered();

export interface DoughnutChartProps {
  data: ChartData<"doughnut", number[], string>;
  ariaLabel: string;
  animationKey?: string;
  externalTooltip?: boolean;
  height?: number;
  cutout?: string;
  semicircle?: boolean;
  options?: ChartOptions<"doughnut">;
  className?: string;
}

export const DoughnutChart = ({
  data,
  ariaLabel,
  animationKey,
  externalTooltip = false,
  height = 220,
  cutout = "58%",
  semicircle = false,
  options,
  className,
}: DoughnutChartProps) => {
  const chartRef = useRef<ChartJS<"doughnut"> | null>(null);
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const mergedOptions = useMemo<ChartOptions<"doughnut">>(
    () => ({
      ...options,
      responsive: true,
      maintainAspectRatio: false,
      cutout,
      rotation: semicircle ? -90 : undefined,
      circumference: semicircle ? 180 : undefined,
      animation: prefersReducedMotion
        ? false
        : {
            animateRotate: true,
            animateScale: false,
            duration: 550,
            easing: "easeOutQuart",
          },
      plugins: {
        ...options?.plugins,
        legend: {
          display: false,
          ...options?.plugins?.legend,
        },
        tooltip: {
          padding: 10,
          cornerRadius: 4,
          titleMarginBottom: 6,
          ...options?.plugins?.tooltip,
          enabled: externalTooltip
            ? false
            : (options?.plugins?.tooltip?.enabled ?? true),
          external: externalTooltip
            ? renderExternalDoughnutTooltip
            : options?.plugins?.tooltip?.external,
        },
      },
      layout: {
        ...options?.layout,
        padding: options?.layout?.padding ?? 4,
      },
    }),
    [cutout, externalTooltip, options, prefersReducedMotion, semicircle],
  );

  useEffect(() => {
    if (animationKey === undefined || prefersReducedMotion) return;

    const chart = chartRef.current;
    if (!chart) return;

    chart.stop();
    chart.reset();
    chart.update();
  }, [animationKey, prefersReducedMotion]);

  return (
    <ChartCanvasContainer
      key={animationKey}
      $animate={animationKey !== undefined}
      $height={height}
      className={className}
    >
      <Doughnut
        ref={chartRef}
        data={data}
        options={mergedOptions}
        role="img"
        aria-label={ariaLabel}
      />
    </ChartCanvasContainer>
  );
};
